import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, GraduationCap, Users, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';

// Color Palette
const colors = {
  gold: '#c9a227',
  goldLight: '#e8d5a3',
  rosaAmatista: '#8a5a7a',
  azulCampus: '#1e3a5f',
  blancoPlatinoStart: '#e0e2e5',
  blancoPlatinoEnd: '#f8f9fa',
  tealArmonizador: '#00675b', // 18-4833 TCX
  cream: '#f8f5f0',
};

// Logo SVG component (same as Navbar)
const LogoSVG = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none">
    <defs>
      <linearGradient id="goldLogoProposal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e8d5a3"/>
        <stop offset="50%" stopColor="#c9a227"/>
        <stop offset="100%" stopColor="#b8922a"/>
      </linearGradient>
      <radialGradient id="glowLogoProposal" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#c9a227" stopOpacity="0.3"/>
        <stop offset="100%" stopColor="#c9a227" stopOpacity="0"/>
      </radialGradient>
      <radialGradient id="lightPointProposal" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="1"/>
        <stop offset="40%" stopColor="#e8d5a3" stopOpacity="0.8"/>
        <stop offset="100%" stopColor="#c9a227" stopOpacity="0"/>
      </radialGradient>
    </defs>
    <circle cx="50" cy="50" r="38" fill="url(#glowLogoProposal)"/>
    <circle cx="50" cy="50" r="28" fill="url(#goldLogoProposal)"/>
    <path d="M38 65 L50 30 L62 65" stroke="#f8f4e8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <circle cx="50" cy="30" r="8" fill="url(#lightPointProposal)"/>
    <circle cx="50" cy="30" r="4" fill="#ffffff"/>
  </svg>
);

const newPhotoUrl = "https://customer-assets.emergentagent.com/job_light-school-1/artifacts/zbiejhmz_Gemini_Generated_Image_1x9m5f1x9m5f1x9m.png";

const HomeProposals = () => {
  return (
    <div className="pt-20">
      {/* Header */}
      <div className="bg-gray-900 text-white py-8 px-6 text-center">
        <h1 className="text-3xl font-bold mb-2">Propuestas de Rediseño - HOME</h1>
        <p className="text-gray-300">Herminio, elige la opción que más te guste para cada sección</p>
      </div>

      {/* ===== SECTION 1: HERO OPTIONS ===== */}
      <div className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">
            🖼️ OPCIÓN HERO - Formato de imagen
          </h2>
          
          {/* Option A: Rectangular with soft edges */}
          <div className="mb-16 p-8 bg-white rounded-2xl shadow-lg">
            <h3 className="text-xl font-bold text-center mb-6 text-[#c9a227]">OPCIÓN A: Rectangular con bordes suaves</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 space-y-6">
                <div className="w-12 h-[2px]" style={{ backgroundColor: colors.gold }} />
                <h1 className="text-4xl lg:text-5xl font-serif text-gray-800 leading-tight">
                  Un espacio para volver a ti.
                  <span className="block" style={{ color: colors.tealArmonizador }}>Sin prisa. Sin ruido.</span>
                </h1>
                <p className="text-lg text-gray-600">
                  De la desconexión y el esfuerzo constante, a habitar tu cuerpo, 
                  tu consciencia y tu vida con claridad y estabilidad.
                </p>
                <div className="flex gap-4">
                  <Button style={{ backgroundColor: colors.gold }} className="text-white px-6 py-5">
                    Descarga el ebook gratuito <ArrowDown className="ml-2 w-4 h-4" />
                  </Button>
                  <Button variant="ghost" className="text-gray-600">
                    Explorar formaciones <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl opacity-20" style={{ backgroundColor: colors.tealArmonizador }} />
                  <img 
                    src={newPhotoUrl}
                    alt="Herminio"
                    className="relative w-full max-w-md mx-auto rounded-3xl shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Option B: Blob/Organic shape */}
          <div className="mb-16 p-8 bg-white rounded-2xl shadow-lg">
            <h3 className="text-xl font-bold text-center mb-6 text-[#c9a227]">OPCIÓN B: Forma orgánica/Blob</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 space-y-6">
                <div className="w-12 h-[2px]" style={{ backgroundColor: colors.gold }} />
                <h1 className="text-4xl lg:text-5xl font-serif text-gray-800 leading-tight">
                  Un espacio para volver a ti.
                  <span className="block" style={{ color: colors.tealArmonizador }}>Sin prisa. Sin ruido.</span>
                </h1>
                <p className="text-lg text-gray-600">
                  De la desconexión y el esfuerzo constante, a habitar tu cuerpo, 
                  tu consciencia y tu vida con claridad y estabilidad.
                </p>
              </div>
              <div className="order-1 lg:order-2 flex justify-center">
                <div className="relative w-80 h-80">
                  {/* Blob shape with clip-path */}
                  <div 
                    className="absolute inset-0 opacity-30"
                    style={{ 
                      backgroundColor: colors.gold,
                      borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
                      animation: 'blob 8s ease-in-out infinite'
                    }}
                  />
                  <img 
                    src={newPhotoUrl}
                    alt="Herminio"
                    className="relative w-full h-full object-cover shadow-xl"
                    style={{ 
                      borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Option C: Full width with overlay */}
          <div className="mb-16 rounded-2xl shadow-lg overflow-hidden">
            <h3 className="text-xl font-bold text-center py-4 bg-white text-[#c9a227]">OPCIÓN C: Imagen grande con overlay de texto</h3>
            <div className="relative h-[500px]">
              <img 
                src={newPhotoUrl}
                alt="Herminio"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-transparent" />
              <div className="relative h-full flex items-center">
                <div className="max-w-xl px-12 space-y-6">
                  <div className="w-12 h-[2px]" style={{ backgroundColor: colors.gold }} />
                  <h1 className="text-4xl lg:text-5xl font-serif text-gray-800 leading-tight">
                    Un espacio para volver a ti.
                    <span className="block" style={{ color: colors.tealArmonizador }}>Sin prisa. Sin ruido.</span>
                  </h1>
                  <p className="text-lg text-gray-600">
                    De la desconexión y el esfuerzo constante, a habitar tu cuerpo, 
                    tu consciencia y tu vida.
                  </p>
                  <div className="flex gap-4">
                    <Button style={{ backgroundColor: colors.gold }} className="text-white px-6 py-5">
                      Descarga el ebook gratuito
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Option D: Asymmetric composition */}
          <div className="mb-16 p-8 bg-white rounded-2xl shadow-lg">
            <h3 className="text-xl font-bold text-center mb-6 text-[#c9a227]">OPCIÓN D: Composición asimétrica (imagen más pequeña, texto protagonista)</h3>
            <div className="relative min-h-[400px]">
              <div className="max-w-2xl space-y-6 pt-8">
                <div className="w-12 h-[2px]" style={{ backgroundColor: colors.gold }} />
                <h1 className="text-5xl lg:text-6xl font-serif text-gray-800 leading-tight">
                  Un espacio para volver a ti.
                  <span className="block mt-2" style={{ color: colors.tealArmonizador }}>Sin prisa. Sin ruido.</span>
                </h1>
                <p className="text-xl text-gray-600 max-w-lg">
                  De la desconexión y el esfuerzo constante, a habitar tu cuerpo, 
                  tu consciencia y tu vida con claridad y estabilidad.
                </p>
                <div className="flex gap-4 pt-4">
                  <Button style={{ backgroundColor: colors.gold }} className="text-white px-8 py-6 text-lg">
                    Descarga el ebook gratuito <ArrowDown className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
              {/* Floating image */}
              <div className="absolute right-0 top-0 w-64 h-72 hidden lg:block">
                <img 
                  src={newPhotoUrl}
                  alt="Herminio"
                  className="w-full h-full object-cover rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== SECTION 2: FEATURED BLOCKS ===== */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">
            🎨 BLOQUES DESTACADOS - Facilitadoras, Campus, Límites
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Cada formación con su color distintivo, invitando a entrar
          </p>
          
          {/* Block Style Preview */}
          <div className="space-y-8">
            
            {/* FACILITADORAS - Rosa Amatista */}
            <div 
              className="rounded-3xl p-8 lg:p-12 text-white shadow-xl hover:shadow-2xl transition-shadow cursor-pointer"
              style={{ backgroundColor: colors.rosaAmatista }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-2 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Formación principal</span>
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">3ª Edición</span>
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-serif">
                    Facilitadoras en Sostén Energético
                  </h3>
                  <p className="text-white/80 text-lg leading-relaxed max-w-xl">
                    Una formación profunda para quienes sienten el llamado de acompañar a otros 
                    en sus procesos de sanación y transformación. 12 módulos, 4 meses.
                  </p>
                  <div className="flex items-center gap-6 pt-4">
                    <span className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5" />
                      12 módulos
                    </span>
                    <span className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Grupo reducido
                    </span>
                  </div>
                  <Button 
                    className="mt-4 bg-white hover:bg-white/90 px-6 py-5"
                    style={{ color: colors.rosaAmatista }}
                  >
                    Conocer más <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
                <div className="hidden lg:flex justify-center">
                  <div className="w-32 h-32 rounded-full bg-white/20 flex items-center justify-center">
                    <GraduationCap className="w-16 h-16 text-white/80" />
                  </div>
                </div>
              </div>
            </div>

            {/* CAMPUS - Azul Profundo */}
            <div 
              className="rounded-3xl p-8 lg:p-12 text-white shadow-xl hover:shadow-2xl transition-shadow cursor-pointer"
              style={{ backgroundColor: colors.azulCampus }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-2 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">Comunidad</span>
                    <span className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: colors.gold, color: '#fff' }}>Próximamente</span>
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-serif">
                    Campus de Interioridad
                  </h3>
                  <p className="text-white/80 text-lg leading-relaxed max-w-xl">
                    Una comunidad mensual de práctica y encuentro. Un espacio para sostener 
                    tu proceso con otros, con contenidos exclusivos y encuentros en vivo.
                  </p>
                  <div className="flex items-center gap-6 pt-4">
                    <span className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Comunidad activa
                    </span>
                    <span className="flex items-center gap-2">
                      22€/mes
                    </span>
                  </div>
                  <Button 
                    className="mt-4 bg-white hover:bg-white/90 px-6 py-5"
                    style={{ color: colors.azulCampus }}
                  >
                    Apuntarme a la lista de espera <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
                <div className="hidden lg:flex justify-center">
                  <div className="w-32 h-32 rounded-full bg-white/20 flex items-center justify-center">
                    <Users className="w-16 h-16 text-white/80" />
                  </div>
                </div>
              </div>
            </div>

            {/* LÍMITES - Blanco Platino */}
            <div 
              className="rounded-3xl p-8 lg:p-12 shadow-xl hover:shadow-2xl transition-shadow cursor-pointer border border-gray-200"
              style={{ 
                background: `linear-gradient(135deg, ${colors.blancoPlatinoStart} 0%, ${colors.blancoPlatinoEnd} 100%)`
              }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-2 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-gray-800/10 rounded-full text-sm text-gray-700">Curso intensivo</span>
                    <span className="px-3 py-1 rounded-full text-sm text-white" style={{ backgroundColor: colors.gold }}>Nueva edición</span>
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-serif text-gray-800">
                    Límites
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed max-w-xl">
                    Un curso para aprender a poner límites desde la claridad y el amor propio.
                    Reconocer tu espacio, comunicar tus necesidades, sostener tu territorio interior.
                  </p>
                  <div className="flex items-center gap-6 pt-4 text-gray-600">
                    <span className="flex items-center gap-2">
                      <Compass className="w-5 h-5" />
                      4 semanas
                    </span>
                    <span className="flex items-center gap-2">
                      14 plazas
                    </span>
                  </div>
                  <Button 
                    className="mt-4 text-white px-6 py-5"
                    style={{ backgroundColor: colors.gold }}
                  >
                    Ver información <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
                <div className="hidden lg:flex justify-center">
                  <div className="w-32 h-32 rounded-full bg-gray-800/10 flex items-center justify-center">
                    <Compass className="w-16 h-16 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== SECTION 3: COLOR PALETTE ===== */}
      <div className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">
            🎨 PALETA DE COLORES UNIFICADA
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="w-full h-24 rounded-xl shadow-lg mb-2" style={{ backgroundColor: colors.gold }} />
              <p className="font-medium">Dorado</p>
              <p className="text-sm text-gray-500">#c9a227</p>
              <p className="text-xs text-gray-400">Hilo conductor</p>
            </div>
            <div className="text-center">
              <div className="w-full h-24 rounded-xl shadow-lg mb-2" style={{ backgroundColor: colors.rosaAmatista }} />
              <p className="font-medium">Rosa Amatista</p>
              <p className="text-sm text-gray-500">#8a5a7a</p>
              <p className="text-xs text-gray-400">Facilitadoras</p>
            </div>
            <div className="text-center">
              <div className="w-full h-24 rounded-xl shadow-lg mb-2" style={{ backgroundColor: colors.azulCampus }} />
              <p className="font-medium">Azul Profundo</p>
              <p className="text-sm text-gray-500">#1e3a5f</p>
              <p className="text-xs text-gray-400">Campus</p>
            </div>
            <div className="text-center">
              <div className="w-full h-24 rounded-xl shadow-lg mb-2 border" style={{ background: `linear-gradient(135deg, ${colors.blancoPlatinoStart}, ${colors.blancoPlatinoEnd})` }} />
              <p className="font-medium">Blanco Platino</p>
              <p className="text-sm text-gray-500">#e0e2e5 → #f8f9fa</p>
              <p className="text-xs text-gray-400">Límites</p>
            </div>
            <div className="text-center">
              <div className="w-full h-24 rounded-xl shadow-lg mb-2" style={{ backgroundColor: colors.tealArmonizador }} />
              <p className="font-medium">Teal Esmeralda</p>
              <p className="text-sm text-gray-500">#00675b</p>
              <p className="text-xs text-gray-400">18-4833 TCX</p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== SECTION 4: FOOTER LOGO FIX ===== */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-800">
            ✅ CORRECCIÓN FOOTER - Logo unificado
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 bg-red-50 rounded-xl border-2 border-red-200">
              <h3 className="font-bold text-red-700 mb-4">❌ ANTES (incorrecto)</h3>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#c9a227] flex items-center justify-center">
                  <span className="text-white font-serif text-lg">A</span>
                </div>
                <span className="font-serif text-xl text-gray-800">
                  Escuela de Consciencia Akáshica
                </span>
              </div>
              <p className="text-sm text-red-600 mt-2">Solo letra "A" en círculo dorado</p>
            </div>
            
            <div className="p-6 bg-green-50 rounded-xl border-2 border-green-200">
              <h3 className="font-bold text-green-700 mb-4">✅ DESPUÉS (correcto)</h3>
              <div className="flex items-center gap-3">
                <LogoSVG className="w-10 h-10" />
                <span className="font-serif text-xl text-gray-800">
                  Escuela de Consciencia Akáshica
                </span>
              </div>
              <p className="text-sm text-green-600 mt-2">Logo SVG completo con esfera y rayos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="py-12 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold mb-4">¿Qué te parece?</h2>
          <p className="text-gray-300 mb-6">
            Dime qué opciones prefieres:
          </p>
          <ul className="text-left max-w-md mx-auto space-y-2 text-gray-300">
            <li>• <strong>Hero:</strong> ¿Opción A, B, C o D?</li>
            <li>• <strong>Bloques:</strong> ¿Te gusta el estilo de los 3 bloques de color?</li>
            <li>• <strong>Color Teal:</strong> ¿Te gusta como armonizador en textos?</li>
            <li>• <strong>Footer:</strong> Confirmo que lo corrijo con el logo SVG</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HomeProposals;
